// Originally from jimp (I think, or maybe some other resize algorithm)
/* eslint-disable no-param-reassign */
import type { Bitmap, WritableBitmap } from "./types.ts";

function* bufferIdentity<T extends ReadIndexBuffer>(
	buffer: T,
): Generator<undefined, T> {
	yield;
	return buffer;
}

type ReadIndexBuffer = {
	readonly [key in number]: number;
};

type BufferLike = Uint8Array | Float64Array | Float32Array;

type Uint8ArraySource = Readonly<Record<number, number>>;

// TODO: This is really bad! Might be modified later
// const defaultBufferLike = new Uint8Array();

const fourthChannel = true;
const channelsNum = 4;

// Configuration and state for resize operation
type ResizeState = {
	widthOriginal: number;
	heightOriginal: number;
	targetWidth: number;
	targetHeight: number;
	targetWidthMultipliedByChannels: number;
	originalWidthMultipliedByChannels: number;
	originalHeightMultipliedByChannels: number;
	widthPassResultSize: number;
	finalResultSize: number;
	resizeWidth: (buffer: Uint8ArraySource) => Generator<undefined, BufferLike>;
	ratioWeightWidthPass: number;
	resizeHeight: (buffer: BufferLike) => Generator<undefined, BufferLike>;
	ratioWeightHeightPass: number;
	widthBuffer: BufferLike;
	heightBuffer: BufferLike;
	outputHeightWorkBench: BufferLike;
	outputHeightWorkBenchOpaquePixelsCount: BufferLike;
	outputWidthWorkBench: BufferLike;
	outputWidthWorkBenchOpaquePixelsCount: BufferLike;
};

function createResizeState(
	widthOriginal: number,
	heightOriginal: number,
	targetWidth: number,
	targetHeight: number,
): ResizeState {
	const finalWidthOriginal = Math.abs(Math.floor(widthOriginal) || 0);
	const finalHeightOriginal = Math.abs(Math.floor(heightOriginal) || 0);
	const finalTargetWidth = Math.abs(Math.floor(targetWidth) || 0);
	const finalTargetHeight = Math.abs(Math.floor(targetHeight) || 0);

	const targetWidthMultipliedByChannels = finalTargetWidth * channelsNum;
	return {
		widthOriginal: finalWidthOriginal,
		heightOriginal: finalHeightOriginal,
		targetWidth: finalTargetWidth,
		targetHeight: finalTargetHeight,
		targetWidthMultipliedByChannels,
		originalWidthMultipliedByChannels: finalWidthOriginal * channelsNum,
		originalHeightMultipliedByChannels: finalHeightOriginal * channelsNum,
		widthPassResultSize: targetWidthMultipliedByChannels * finalHeightOriginal,
		finalResultSize: targetWidthMultipliedByChannels * finalTargetHeight,
		resizeWidth: bufferIdentity as any,
		ratioWeightWidthPass: 0,
		resizeHeight: bufferIdentity,
		ratioWeightHeightPass: 0,
		widthBuffer: undefined as any,
		heightBuffer: undefined as any,
		outputHeightWorkBench: undefined as any,
		outputHeightWorkBenchOpaquePixelsCount: undefined as any,
		outputWidthWorkBench: undefined as any,
		outputWidthWorkBenchOpaquePixelsCount: undefined as any,
	};
}

function generateFloatBuffer(bufferLength: number): Float32Array {
	return new Float32Array(bufferLength);
}

function generateFloat64Buffer(bufferLength: number): Float64Array {
	return new Float64Array(bufferLength);
}

function generateUint8Buffer(bufferLength: number): Uint8Array {
	return new Uint8Array(bufferLength);
}

function initializeFirstPassBuffers(state: ResizeState, BILINEARAlgo: boolean) {
	// Initialize the internal width pass buffers:
	state.widthBuffer = generateFloatBuffer(state.widthPassResultSize);

	if (!BILINEARAlgo) {
		state.outputWidthWorkBench = generateFloatBuffer(
			state.originalHeightMultipliedByChannels,
		);

		if (channelsNum > 3) {
			state.outputWidthWorkBenchOpaquePixelsCount = generateFloat64Buffer(
				state.heightOriginal,
			);
		}
	}
}

function initializeSecondPassBuffers(
	state: ResizeState,
	BILINEARAlgo: boolean,
) {
	// Initialize the internal height pass buffers:
	state.heightBuffer = generateUint8Buffer(state.finalResultSize);

	if (!BILINEARAlgo) {
		state.outputHeightWorkBench = generateFloatBuffer(
			state.targetWidthMultipliedByChannels,
		);

		if (channelsNum > 3) {
			state.outputHeightWorkBenchOpaquePixelsCount = generateFloat64Buffer(
				state.targetWidth,
			);
		}
	}
}

function _resizeWidthInterpolatedRGBChannels(
	state: ResizeState,
	source: Uint8ArraySource,
): BufferLike {
	// const channelsNum = fourthChannel ? 4 : 3;
	const ratioWeight = state.ratioWeightWidthPass;
	const outputBuffer = state.widthBuffer;
	let weight = 0;
	let finalOffset = 0;
	let pixelOffset = 0;
	let firstWeight = 0;
	let secondWeight = 0;
	let targetPosition: number; // Handle for only one interpolation input being valid for start calculation:

	for (
		targetPosition = 0;
		weight < 1 / 3;
		targetPosition += channelsNum, weight += ratioWeight
	) {
		for (
			finalOffset = targetPosition, pixelOffset = 0;
			finalOffset < state.widthPassResultSize;
			pixelOffset += state.originalWidthMultipliedByChannels,
				finalOffset += state.targetWidthMultipliedByChannels
		) {
			outputBuffer[finalOffset] = source[pixelOffset];
			outputBuffer[finalOffset + 1] = source[pixelOffset + 1];
			outputBuffer[finalOffset + 2] = source[pixelOffset + 2];
			if (fourthChannel)
				outputBuffer[finalOffset + 3] = source[pixelOffset + 3];
		}
	} // Adjust for overshoot of the last pass's counter:

	weight -= 1 / 3;
	let interpolationWidthSourceReadStop: number;

	for (
		interpolationWidthSourceReadStop = state.widthOriginal - 1;
		weight < interpolationWidthSourceReadStop;
		targetPosition += channelsNum, weight += ratioWeight
	) {
		// Calculate weightings:
		secondWeight = weight % 1;
		firstWeight = 1 - secondWeight; // Interpolate:

		for (
			finalOffset = targetPosition,
				pixelOffset = Math.floor(weight) * channelsNum;
			finalOffset < state.widthPassResultSize;
			pixelOffset += state.originalWidthMultipliedByChannels,
				finalOffset += state.targetWidthMultipliedByChannels
		) {
			outputBuffer[finalOffset + 0] =
				source[pixelOffset + 0] * firstWeight +
				source[pixelOffset + channelsNum + 0] * secondWeight;
			outputBuffer[finalOffset + 1] =
				source[pixelOffset + 1] * firstWeight +
				source[pixelOffset + channelsNum + 1] * secondWeight;
			outputBuffer[finalOffset + 2] =
				source[pixelOffset + 2] * firstWeight +
				source[pixelOffset + channelsNum + 2] * secondWeight;
			if (fourthChannel)
				outputBuffer[finalOffset + 3] =
					source[pixelOffset + 3] * firstWeight +
					source[pixelOffset + channelsNum + 3] * secondWeight;
		}
	} // Handle for only one interpolation input being valid for end calculation:

	for (
		interpolationWidthSourceReadStop =
			state.originalWidthMultipliedByChannels - channelsNum;
		targetPosition < state.targetWidthMultipliedByChannels;
		targetPosition += channelsNum
	) {
		for (
			finalOffset = targetPosition,
				pixelOffset = interpolationWidthSourceReadStop;
			finalOffset < state.widthPassResultSize;
			pixelOffset += state.originalWidthMultipliedByChannels,
				finalOffset += state.targetWidthMultipliedByChannels
		) {
			outputBuffer[finalOffset] = source[pixelOffset];
			outputBuffer[finalOffset + 1] = source[pixelOffset + 1];
			outputBuffer[finalOffset + 2] = source[pixelOffset + 2];
			if (fourthChannel)
				outputBuffer[finalOffset + 3] = source[pixelOffset + 3];
		}
	}

	return outputBuffer;
}

function resizeWidthRgbaChannelsLoop(
	state: ResizeState,
	buffer: Uint8ArraySource,
	calcVars: {
		ratioWeight: number;
		ratioWeightDivisor: number;
		nextLineOffsetOriginalWidth: number;
		nextLineOffsetTargetWidth: number;
		output: BufferLike;
		outputBuffer: BufferLike;
		trustworthyColorsCount: BufferLike;
		weight: number;
		amountToNext: number;
		actualPosition: number;
		currentPosition: number;
		outputOffset: number;
		multiplier: number;
	},
) {
	for (let line = 0; line < state.originalHeightMultipliedByChannels; ) {
		calcVars.output[line++] = 0;
		calcVars.output[line++] = 0;
		calcVars.output[line++] = 0;

		if (fourthChannel) {
			calcVars.output[line++] = 0;
			calcVars.trustworthyColorsCount[line / channelsNum - 1] = 0;
		}
	}

	calcVars.weight = calcVars.ratioWeight;

	do {
		calcVars.amountToNext =
			1 + calcVars.actualPosition - calcVars.currentPosition;
		calcVars.multiplier = Math.min(calcVars.weight, calcVars.amountToNext);

		let pixelOffset1 = calcVars.actualPosition;
		for (
			let line = 0;
			line < state.originalHeightMultipliedByChannels;
			pixelOffset1 += calcVars.nextLineOffsetOriginalWidth
		) {
			const r = buffer[pixelOffset1];
			const g = buffer[++pixelOffset1];
			const b = buffer[++pixelOffset1];
			const a = fourthChannel ? buffer[++pixelOffset1] : 255; // Ignore RGB values if pixel is completely transparent

			calcVars.output[line++] += (a ? r : 0) * calcVars.multiplier;
			calcVars.output[line++] += (a ? g : 0) * calcVars.multiplier;
			calcVars.output[line++] += (a ? b : 0) * calcVars.multiplier;

			if (fourthChannel) {
				calcVars.output[line++] += a * calcVars.multiplier;
				calcVars.trustworthyColorsCount[line / channelsNum - 1] += a
					? calcVars.multiplier
					: 0;
			}
		}

		if (calcVars.weight >= calcVars.amountToNext) {
			calcVars.actualPosition += channelsNum;
			calcVars.currentPosition = calcVars.actualPosition;
			calcVars.weight -= calcVars.amountToNext;
		} else {
			calcVars.currentPosition += calcVars.weight;
			break;
		}
	} while (
		calcVars.weight > 0 &&
		calcVars.actualPosition < state.originalWidthMultipliedByChannels
	);

	let pixelOffset = calcVars.outputOffset;
	for (
		let line = 0;
		line < state.originalHeightMultipliedByChannels;
		pixelOffset += calcVars.nextLineOffsetTargetWidth
	) {
		calcVars.weight = fourthChannel
			? calcVars.trustworthyColorsCount[line / channelsNum]
			: 1;
		calcVars.multiplier = fourthChannel
			? calcVars.weight
				? 1 / calcVars.weight
				: 0
			: calcVars.ratioWeightDivisor;
		calcVars.outputBuffer[pixelOffset] =
			calcVars.output[line++] * calcVars.multiplier;
		calcVars.outputBuffer[++pixelOffset] =
			calcVars.output[line++] * calcVars.multiplier;
		calcVars.outputBuffer[++pixelOffset] =
			calcVars.output[line++] * calcVars.multiplier;
		if (fourthChannel) {
			calcVars.outputBuffer[++pixelOffset] =
				calcVars.output[line++] * calcVars.ratioWeightDivisor;
		}
	}
	calcVars.outputOffset += channelsNum;
}

function* _resizeWidthRGBChannels(
	state: ResizeState,
	buffer: Uint8ArraySource,
): Generator<undefined, BufferLike> {
	// const channelsNum = fourthChannel ? 4 : 3;
	const calcVars = {
		ratioWeight: state.ratioWeightWidthPass,
		ratioWeightDivisor: 1 / state.ratioWeightWidthPass,
		nextLineOffsetOriginalWidth:
			state.originalWidthMultipliedByChannels - channelsNum + 1,
		nextLineOffsetTargetWidth:
			state.targetWidthMultipliedByChannels - channelsNum + 1,
		output: state.outputWidthWorkBench,
		outputBuffer: state.widthBuffer,
		trustworthyColorsCount: state.outputWidthWorkBenchOpaquePixelsCount,
		weight: 0,
		amountToNext: 0,
		actualPosition: 0,
		currentPosition: 0,
		outputOffset: 0,
		multiplier: 1,
	};

	do {
		resizeWidthRgbaChannelsLoop(state, buffer, calcVars);
		yield;
	} while (calcVars.outputOffset < state.targetWidthMultipliedByChannels);

	return calcVars.outputBuffer;
}

function* resizeWidthInterpolatedRGBA(
	state: ResizeState,
	source: Uint8ArraySource,
): Generator<undefined, BufferLike> {
	yield;
	return _resizeWidthInterpolatedRGBChannels(state, source /* , true */);
}

function resizeHeightLoop(
	state: ResizeState,
	source: ReadIndexBuffer,
	calcVars: {
		ratioWeightDivisor: number;
		actualPosition: number;
		currentPosition: number;
		outputOffset: number;
	},
): void {
	for (let i = 0; i < state.targetWidthMultipliedByChannels; ) {
		state.outputHeightWorkBench[i++] = 0;
		state.outputHeightWorkBench[i++] = 0;
		state.outputHeightWorkBench[i++] = 0;

		if (fourthChannel) {
			state.outputHeightWorkBench[i++] = 0;
			state.outputHeightWorkBenchOpaquePixelsCount[i / 4 - 1] = 0;
		}
	}

	let weight = state.ratioWeightHeightPass;

	do {
		const amountToNext = 1 + calcVars.actualPosition - calcVars.currentPosition;
		const multiplier = Math.min(weight, amountToNext);
		let caret = calcVars.actualPosition;

		for (let j = 0; j < state.targetWidthMultipliedByChannels; ) {
			const r = source[caret++];
			const g = source[caret++];
			const b = source[caret++];
			const a = fourthChannel ? source[caret++] : 255; // Ignore RGB values if pixel is completely transparent

			state.outputHeightWorkBench[j++] += (a ? r : 0) * multiplier;
			state.outputHeightWorkBench[j++] += (a ? g : 0) * multiplier;
			state.outputHeightWorkBench[j++] += (a ? b : 0) * multiplier;

			if (fourthChannel) {
				state.outputHeightWorkBench[j++] += a * multiplier;
				state.outputHeightWorkBenchOpaquePixelsCount[j / 4 - 1] += a
					? multiplier
					: 0;
			}
		}

		if (weight >= amountToNext) {
			calcVars.actualPosition = caret;
			calcVars.currentPosition = calcVars.actualPosition;
			weight -= amountToNext;
		} else {
			calcVars.currentPosition += weight;
			break;
		}
	} while (weight > 0 && calcVars.actualPosition < state.widthPassResultSize);

	for (let k = 0; k < state.targetWidthMultipliedByChannels; ) {
		const pixelWeight = fourthChannel
			? state.outputHeightWorkBenchOpaquePixelsCount[k / 4]
			: 1;
		const multiplier = fourthChannel
			? pixelWeight
				? 1 / pixelWeight
				: 0
			: calcVars.ratioWeightDivisor;
		state.heightBuffer[calcVars.outputOffset++] = Math.round(
			state.outputHeightWorkBench[k++] * multiplier,
		);
		state.heightBuffer[calcVars.outputOffset++] = Math.round(
			state.outputHeightWorkBench[k++] * multiplier,
		);
		state.heightBuffer[calcVars.outputOffset++] = Math.round(
			state.outputHeightWorkBench[k++] * multiplier,
		);

		if (fourthChannel) {
			state.heightBuffer[calcVars.outputOffset++] = Math.round(
				state.outputHeightWorkBench[k++] * calcVars.ratioWeightDivisor,
			);
		}
	}
}

function* _resizeHeightRGBChannels(
	state: ResizeState,
	buffer: BufferLike,
): Generator<undefined, BufferLike> {
	const calcVars = {
		ratioWeightDivisor: 1 / state.ratioWeightHeightPass,
		actualPosition: 0,
		currentPosition: 0,
		outputOffset: 0,
	};

	do {
		resizeHeightLoop(state, buffer, calcVars);
		yield;
	} while (calcVars.outputOffset < state.finalResultSize);

	return state.heightBuffer;
}

function* resizeHeightInterpolated(
	state: ResizeState,
	source: ReadIndexBuffer,
): Generator<undefined, BufferLike> {
	const ratioWeight = state.ratioWeightHeightPass;
	const outputBuffer = state.heightBuffer;
	let weight = 0;
	let finalOffset = 0;
	let pixelOffset = 0;
	let pixelOffsetAccumulated = 0;
	let pixelOffsetAccumulated2 = 0;
	let firstWeight = 0;
	let secondWeight = 0;
	let interpolationHeightSourceReadStop: number; // Handle for only one interpolation input being valid for start calculation:

	for (; weight < 1 / 3; weight += ratioWeight) {
		for (
			pixelOffset = 0;
			pixelOffset < state.targetWidthMultipliedByChannels;
		) {
			outputBuffer[finalOffset++] = Math.round(source[pixelOffset++]);
		}
	} // Adjust for overshoot of the last pass's counter:

	yield;

	weight -= 1 / 3;

	for (
		interpolationHeightSourceReadStop = state.heightOriginal - 1;
		weight < interpolationHeightSourceReadStop;
		weight += ratioWeight
	) {
		// Calculate weightings:
		secondWeight = weight % 1;
		firstWeight = 1 - secondWeight; // Interpolate:

		pixelOffsetAccumulated =
			Math.floor(weight) * state.targetWidthMultipliedByChannels;
		pixelOffsetAccumulated2 =
			pixelOffsetAccumulated + state.targetWidthMultipliedByChannels;

		for (
			pixelOffset = 0;
			pixelOffset < state.targetWidthMultipliedByChannels;
			++pixelOffset
		) {
			outputBuffer[finalOffset++] = Math.round(
				source[pixelOffsetAccumulated++] * firstWeight +
					source[pixelOffsetAccumulated2++] * secondWeight,
			);
		}
	} // Handle for only one interpolation input being valid for end calculation:

	while (finalOffset < state.finalResultSize) {
		for (
			pixelOffset = 0,
				pixelOffsetAccumulated =
					interpolationHeightSourceReadStop *
					state.targetWidthMultipliedByChannels;
			pixelOffset < state.targetWidthMultipliedByChannels;
			++pixelOffset
		) {
			outputBuffer[finalOffset++] = Math.round(
				source[pixelOffsetAccumulated++],
			);
		}
	}

	return outputBuffer;
}

function configurePasses(state: ResizeState) {
	if (state.widthOriginal === state.targetWidth) {
		// Bypass the width resizer pass:
		state.resizeWidth = bufferIdentity as any;
	} else {
		// Setup the width resizer pass:
		state.ratioWeightWidthPass = state.widthOriginal / state.targetWidth;

		if (state.ratioWeightWidthPass < 1) {
			initializeFirstPassBuffers(state, true);
			state.resizeWidth = (buffer: Uint8ArraySource) =>
				resizeWidthInterpolatedRGBA(state, buffer);
		} else {
			initializeFirstPassBuffers(state, false);
			state.resizeWidth = (buffer: Uint8ArraySource) =>
				_resizeWidthRGBChannels(state, buffer);
		}
	}

	if (state.heightOriginal === state.targetHeight) {
		// Bypass the height resizer pass:
		state.resizeHeight = bufferIdentity;
	} else {
		// Setup the height resizer pass:
		state.ratioWeightHeightPass = state.heightOriginal / state.targetHeight;

		if (state.ratioWeightHeightPass < 1) {
			initializeSecondPassBuffers(state, true);
			state.resizeHeight = (buffer: BufferLike) =>
				resizeHeightInterpolated(state, buffer);
		} else {
			initializeSecondPassBuffers(state, false);
			state.resizeHeight = (buffer: BufferLike) =>
				_resizeHeightRGBChannels(state, buffer);
		}
	}
}

function initialize(state: ResizeState) {
	// Perform some checks:
	if (
		state.widthOriginal > 0 &&
		state.heightOriginal > 0 &&
		state.targetWidth > 0 &&
		state.targetHeight > 0
	) {
		configurePasses(state);
	} else {
		throw new Error("Invalid settings specified for the resizer.");
	}
}

function* resize(
	state: ResizeState,
	source: Uint8ArraySource,
): Generator<undefined, Uint8Array> {
	const widthBuffer = yield* state.resizeWidth(source);
	const heightResult = yield* state.resizeHeight(widthBuffer);

	// When the height pass actually ran, heightResult is state.heightBuffer: a
	// freshly-allocated, rounded Uint8Array we own. Return it directly rather
	// than copying it into another Uint8Array. The identity paths still need the
	// copy/conversion (Float32 width-pass output, or aliasing the input source).
	if (state.resizeHeight !== bufferIdentity) {
		if (!(heightResult instanceof Uint8Array)) {
			throw new Error("Invalid state");
		}
		return heightResult;
	}

	return new Uint8Array(heightResult);
}

function* resizeBitmapToWritable<T extends Bitmap>(
	image: T,
	width: number,
	height: number,
): Generator<undefined, T & WritableBitmap> {
	if (
		width <= 0 ||
		height <= 0 ||
		Number.isNaN(width) ||
		Number.isNaN(height) ||
		width === Infinity ||
		height === Infinity
	) {
		throw new Error(
			`Invalid resize: ${width}x${height} from ${image.width}x${image.height}`,
		);
	}

	if (width !== Math.round(width) || height !== Math.round(height)) {
		throw new Error(`Resize expects int width height ${width}x${height}`);
	}

	const state = createResizeState(image.width, image.height, width, height);
	initialize(state);
	const data = yield* resize(state, image.data);
	return {
		...image,
		data,
		width,
		height,
	};
}

function* resizeBitmap<T extends Bitmap>(
	image: T,
	width: number,
	height: number,
): Generator<undefined, T & WritableBitmap> {
	return yield* resizeBitmapToWritable(image, width, height);
}

export { resizeBitmap, resizeBitmapToWritable };
